/**
 * Co-ord upsell: choosing a size from the dropdown — or pressing Add to bag on
 * a single-variant co-ord — adds that variant to the basket and opens the cart
 * drawer.
 *
 * Mirrors what product-form.js does on a normal add to cart: it requests the
 * rendered cart-items sections alongside the add, then dispatches a
 * `cart:update` event carrying them, so the drawer renders the new line item
 * rather than stale content.
 */
(function () {
  'use strict';

  /**
   * Storefront copy, from the translations the theme exposes on the global
   * Theme object in snippets/scripts.liquid.
   *
   * Shopify's `t` filter escapes any translation whose key does not end in
   * _html, so a string with an apostrophe arrives here as "we&#39;ll". This
   * copy is written out with textContent, which shows entities literally, so
   * decode once here rather than at every call site.
   * @param {string} key
   */
  function t(key) {
    return decodeEntities(
      (typeof Theme !== 'undefined' && Theme.translations && Theme.translations[key]) || ''
    );
  }

  /**
   * A detached textarea decodes entities without parsing markup: its content
   * is plain text by definition, so nothing in the string can become an
   * element.
   * @param {string} value
   */
  function decodeEntities(value) {
    if (!value || value.indexOf('&') === -1) return value;

    var decoder = document.createElement('textarea');
    decoder.innerHTML = value;
    return decoder.value;
  }
  
  var SELECT_SELECTOR = '[data-coord-select]';
  var ADD_SELECTOR = '[data-coord-add]';
  var ADD_URL = '/cart/add.js';
  var CART_URL = '/cart.js';

  /** Section ids the theme needs re-rendered after a cart change. */
  function cartSectionIds() {
    var ids = [];
    document.querySelectorAll('cart-items-component').forEach(function (item) {
      if (item.dataset && item.dataset.sectionId) ids.push(item.dataset.sectionId);
    });
    return ids;
  }

  function openCartDrawer() {
    var drawer = document.querySelector('cart-drawer-component');
    if (!drawer) return false;

    if (typeof drawer.open === 'function') drawer.open();
    else drawer.setAttribute('open', '');
    return true;
  }

  function setStatus(root, message) {
    var status = root.querySelector('[data-coord-status]');
    if (status) status.textContent = message || '';
  }

  /**
   * @param {Element} root - the <coord-upsell> element
   * @param {string} variantId
   * @param {Element|null} control - reset to its placeholder if the add fails
   */
  function addToCart(root, variantId, control) {
    if (!variantId || root.getAttribute('aria-busy') === 'true') return;

    root.setAttribute('aria-busy', 'true');
    setStatus(root, '');

    var routes = (typeof Theme !== 'undefined' && Theme.routes) || {};
    var sections = cartSectionIds();

    var body = { items: [{ id: Number(variantId), quantity: 1 }] };
    if (sections.length) body.sections = sections.join(',');

    fetch(routes.cart_add_url || ADD_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    })
      .then(function (response) {
        return response.json().then(function (payload) {
          // The cart API returns 4xx with a description for real errors.
          if (!response.ok || payload.status) {
            throw new Error(payload.description || payload.message || '');
          }
          return payload;
        });
      })
      .then(function (payload) {
        return fetch(routes.cart_url ? routes.cart_url + '.js' : CART_URL)
          .then(function (response) {
            return response.json();
          })
          .then(function (cart) {
            document.dispatchEvent(
              new CustomEvent('cart:update', {
                bubbles: true,
                detail: {
                  resource: cart,
                  sourceId: 'coord-upsell',
                  data: {
                    source: 'product-form-component',
                    itemCount: 1,
                    variantId: variantId,
                    productId: root.dataset.productId,
                    sections: payload.sections
                  }
                }
              })
            );

            // Reset the dropdown so the placeholder shows again for a second add.
            if (control && control.tagName === 'SELECT') control.value = '';

            // The drawer auto-opens only when the theme setting is on, so open
            // it explicitly. Falls back to the cart page if there is no drawer.
            if (!openCartDrawer()) window.location.href = routes.cart_url || '/cart';
          });
      })
      .catch(function (error) {
        if (control && control.tagName === 'SELECT') control.value = '';
        setStatus(root, error.message || t('coord_upsell_add_error'));
      })
      .finally(function () {
        root.setAttribute('aria-busy', 'false');
      });
  }

  // Delegated, so the block works wherever it is placed and after re-renders.
  document.addEventListener('change', function (event) {
    var select = event.target.closest(SELECT_SELECTOR);
    if (!select || !select.value) return;

    var root = select.closest('coord-upsell');
    if (root) addToCart(root, select.value, select);
  });

  document.addEventListener('click', function (event) {
    var button = event.target.closest(ADD_SELECTOR);
    if (!button || button.disabled) return;

    var root = button.closest('coord-upsell');
    if (!root) return;

    event.preventDefault();
    addToCart(root, button.dataset.coordVariant, button);
  });
})();
